<!-- The cloud could not store what this device reported. Shown outside the payload block
     below, because the payload is exactly what may be missing or stale. -->
@if (checkInHealth(); as health) {
<p-message [severity]="checkInWriteFailed() ? 'error' : 'warn'" [closable]="false" class="mb-4">
    <div class="flex items-start gap-2">
        <i [class]="checkInWriteFailed() ? 'pi pi-database' : 'pi pi-exclamation-triangle'"></i>
        <div>
            <strong>{{ checkInHealthTitle() }}</strong>
            <div class="text-sm mt-1">{{ checkInHealthSummary() }}</div>
            @if (health.detail) {
            <div class="text-sm mt-1 font-mono">{{ health.detail }}</div>
            } @if (checkInHealthOccurrenceNote()) {
            <div class="text-sm mt-1 text-color-secondary">{{ checkInHealthOccurrenceNote() }}</div>
            }
        </div>
    </div>
</p-message>
} @if (device) {
<header class="overview-header">
    <div>
        <p class="overview-eyebrow">System overview</p>
        <h1>{{ displayName(device) }}</h1>
    </div>
    <nav class="overview-actions" aria-label="System quick actions">
        <a class="overview-action" [routerLink]="['/device', device.id, 'linked-devices']"><i class="pi pi-server" aria-hidden="true"></i>Connected devices</a>
        <a class="overview-action" [routerLink]="['/device', device.id, 'config']"><i class="pi pi-cog" aria-hidden="true"></i>Configuration</a>
        @if (localDashboardUrl()) {
        <button type="button" class="overview-action" (click)="openLocalDashboardDialog()"><i class="pi pi-external-link" aria-hidden="true"></i>Local dashboard</button>
        }
    </nav>
</header>
<section class="health-overview" aria-label="Overall status">
    <div [class]="'health-symbol ' + overviewHealth().tone"><i [class]="overviewHealth().icon" aria-hidden="true"></i></div>
    <div class="health-copy" role="status">
        <p class="overview-eyebrow">Overall status</p>
        <h2>{{ overviewHealth().title }}</h2>
        <p>{{ overviewHealth().detail }}</p>
    </div>
    <a class="overview-action" [routerLink]="['/device', device.id, 'messages']">Diagnostics<i class="pi pi-arrow-right" aria-hidden="true"></i></a>
</section>
@if (errorAndWarnings().length > 0) {
<section class="system-status-section">
    <div class="system-status-section-header flex items-center justify-between mb-4">
        <h2>Recent errors and warnings</h2>
        <a class="overview-action" [routerLink]="['/device', device.id, 'messages']">View all messages</a>
    </div>
    <app-messages-panel [messages]="errorAndWarnings()" [count]="5" [subdued]="true"></app-messages-panel>
</section>
}
<section class="system-status-section processor-rooms" aria-labelledby="processor-rooms-heading">
    <h2 id="processor-rooms-heading">Rooms controlled by this processor</h2>
    @if (roomsLoading()) {
    <app-loading-skeleton preset="tiles" [count]="2" label="Loading processor rooms" />
    } @else if (processorRooms().length) {
    <ul class="room-links">
        @for (room of processorRooms(); track room.id) {
        <li>
            <a class="overview-action" [routerLink]="['/dashboard', room.appNamespaceId, room.id]">
                <i class="pi pi-map-marker" aria-hidden="true"></i>
                <span class="room-copy">{{ room.name }}@if (room.parentName) {<small>{{ room.parentName }}</small>}</span>
                <i class="pi pi-arrow-right" aria-hidden="true"></i>
            </a>
        </li>
        }
    </ul>
    } @else {
    <app-empty-state icon="pi pi-map-marker" title="No linked rooms yet" description="Rooms appear here once the processor reports them and they are synced to this tenant's locations." [compact]="true" />
    }
</section>
} @if(p) {
<section class="system-status-section">
    <h2>Performance</h2>
    <div class="status-cards-grid">
        <app-info-widget title="CPU Usage" icon="microchip">
            <app-info-bargraph [percentage]="cpuPercent(p)" [subText]="(cpuPercent(p) | number:'1.0-0') + '%'" badgeText="{{p.environment?.processorCount}} Cores"></app-info-bargraph>
        </app-info-widget>
        <app-info-widget title="Memory Usage" icon="memory">
            <app-info-bargraph [percentage]="memoryPercent(p)" [subText]="memoryText(p)"></app-info-bargraph>
        </app-info-widget>
        <app-info-widget title="Disk Storage" icon="hard-drive">
            <app-info-bargraph [percentage]="diskPercent(p)" [subText]="diskText(p)"></app-info-bargraph>
        </app-info-widget>
        <app-info-widget title="System Uptime" icon="timer">
            <p class="time-text">{{uptime().humanize()}}</p>
            <p class="status-detail-text">{{uptimeDetailed()}}</p>
        </app-info-widget>
        <app-info-widget class="monitor-tile" [title]="'System monitor · ' + windowLabel()" icon="chart-line">
            <app-system-monitor-spark [stats]="stats()" [loading]="statsLoading()" [expanded]="monitorExpanded()" (expand)="toggleMonitor()" />
        </app-info-widget>
    </div>
</section>
@if (monitorExpanded()) {
<section class="system-status-section system-monitor flex flex-col" aria-label="System monitor chart">
    <div class="system-status-section-header flex items-center justify-between mb-4">
        <h2>System monitor</h2>
        <div class="flex items-center gap-2">
            <p-select-button [options]="systemMonitorOptions" [(ngModel)]="windowHoursMode"></p-select-button>
            <p-button icon="pi pi-times" [text]="true" [rounded]="true" severity="secondary" ariaLabel="Hide full chart" (onClick)="toggleMonitor()" />
        </div>
    </div>
    @if(statsLoading() && stats().length === 0) {
    <p class="text-sm text-color-secondary" role="status">Loading system statistics...</p>
    } @else if(stats().length > 0) {
    <app-system-monitor-chart [stats]="stats()" [windowHours]="windowHoursMode"></app-system-monitor-chart>
    } @else {
    <div class="empty-stats text-sm text-color-secondary">No stats available in this window.</div>
    }
</section>
}
<div class="overview-pair">
<section class="system-status-section">
    <h2>System details</h2>
    <div class="status-cards-grid secondary-status-grid">
        <app-info-widget title="Local Time" icon="clock">
            <p class="time-text">{{localTime()}}</p>
            <p class="status-detail-text">{{timeZoneInfo()}}</p>
        </app-info-widget>
        <app-info-widget title="Firmware" icon="info-circle">
            <div class="version-text">{{ firmwareVersion(p) }}</div>
            <div class="status-detail-text">{{ p.model }}</div>
        </app-info-widget>
        <app-info-widget title="App Version" icon="sd-card">
            <div class="version-text">{{ appVersion(p) }}</div>
            <div class="status-detail-text">{{ appAssemblyVersion(p) }}</div>
        </app-info-widget>
        <app-info-widget title="Processor" icon="server">
            <div class="version-text">{{ p.model || device?.model || '—' }}</div>
            <div class="status-detail-text">{{ serialNumber(p) }}</div>
        </app-info-widget>
    </div>
</section>
<section class="system-status-section">
    <h2>Connectivity and location</h2>
    <div class="status-cards-grid secondary-status-grid connection-grid">
        <app-info-widget title="Network" icon="ethernet">
            <div class="version-text">
                {{ ipAddress(p) }}
            </div>
            <div class="status-detail-text">{{ p?.network?.hostName || '—' }}</div>
            @if (publicIp()) {
            <div class="status-detail-text">
                Public IP: {{ publicIp() }}
                <app-ping-link [host]="publicIp()" />
            </div>
            }
            <div class="status-detail-text">{{ p?.processorId || '—' }}</div>
        </app-info-widget>
        <app-info-widget title="Last Seen" icon="eye">
            <div class="time-text">{{ lastSeen(p) }}</div>
            @if (tunnelConnected() === true) {
            <div class="tunnel-status mt-2">
                <i class="pi pi-cloud tunnel-connected"></i>
                <span class="text-sm tunnel-connected">Tunnel Connected</span>
            </div>
            }
        </app-info-widget>
        <app-info-widget title="Location" icon="location-dot">
            @if (resolvedGeo(); as geo) {
            <div class="location-place" [title]="geoPlace(geo)">{{ geoPlace(geo) }}</div>
            <div class="status-detail-text location-source" [class.location-source--approx]="geo.source === 'ipEstimate'">{{ geoSourceLabel() }}</div>
            } @else {
            <div class="location-place location-place--empty">Not set</div>
            <div class="status-detail-text">Set it on the room or building in its Settings tab and this system will inherit it.</div>
            }
            <div class="location-actions">
                @if (resolvedGeo(); as geo) {
                <a class="location-action" [href]="mapsUrl(geo)" target="_blank" rel="noopener noreferrer">Open in Maps <i class="pi pi-external-link" aria-hidden="true"></i></a>
                }
                <button type="button" class="location-action" (click)="openGeoDialog()">{{ device?.geoOverride ? 'Change override' : 'Override location' }}</button>
            </div>
        </app-info-widget>
    </div>
</section>
</div>
} @else if (device) {
<app-empty-state icon="pi pi-wave-pulse" title="No telemetry yet" description="This system has not completed a check-in. CPU, memory, storage and network details appear after the first one." />
} @else {
<app-loading-skeleton preset="tiles" [count]="8" label="Loading system overview" />
}

<!-- Local Dashboard Dialog -->
<p-dialog header="Local Dashboard Access" [(visible)]="showLocalDashboardDialog" [modal]="true" [style]="{ width: '450px' }">
    <div class="flex flex-col gap-4">
        <p class="text-color-secondary">The local dashboard is only accessible when you are on the same network as the device.</p>

        <div class="flex items-center gap-2">
            <span class="font-medium">URL:</span>
            <code class="text-sm bg-surface-100 dark:bg-surface-800 px-2 py-1 rounded">{{ localDashboardUrl() }}</code>
        </div>

        <div class="flex items-center gap-2">
            <span class="font-medium">Status:</span>
            @if (checkingReachability()) {
            <i class="pi pi-spin pi-spinner text-primary"></i>
            <span class="text-color-secondary">Checking reachability...</span>
            } @else if (localDashboardReachable() === true) {
            <i class="pi pi-check-circle text-status-online"></i>
            <span class="text-status-online">Reachable</span>
            } @else if (localDashboardReachable() === false) {
            <i class="pi pi-times-circle text-status-warning"></i>
            <span class="text-status-warning">Not reachable from this network</span>
            }
        </div>

        @if (localDashboardReachable() === false) {
        <p-message severity="warn"> The device may not be reachable from your current network. You may still proceed if you know the device is accessible. </p-message>
        }
    </div>

    <ng-template #footer>
        <p-button label="Cancel" severity="secondary" (click)="showLocalDashboardDialog.set(false)"></p-button>
        <p-button label="Open Dashboard" icon="pi pi-external-link" (click)="proceedToLocalDashboard()" [disabled]="checkingReachability()"></p-button>
    </ng-template>
</p-dialog>

<!-- Geo Location Edit Dialog -->
<app-geo-location-dialog [visible]="geoDialogVisible()" (visibleChange)="geoDialogVisible.set($event)" [deviceId]="device?.id ?? null" [deviceName]="displayName(device)" [initial]="resolvedGeo()" [hasOverride]="!!device?.geoOverride">
</app-geo-location-dialog>
